import {
  CalendarSyncChange,
  CalendarSyncWrite,
  getCalendarSyncWriteOps,
} from '@griply/services';
import {
  CalendarDetailsVisibility,
  CalendarRef,
  ID,
  LinkedItemType,
  SyncMode,
} from '@griply/types';
import {
  canConnectionWriteEvents,
  getConnectionsNeedingReconnect,
  getSortedCalendarSyncs,
  getWritableConnectionCalendars,
} from '@griply/utils';
import { Typography } from '@mui/material';
import {
  useCalendarSyncs,
  useExternalCalendarConnections,
  useRemoveCalendarSync,
  useSetCalendarSync,
  useTwoWaySyncAllowed,
} from 'features/external-connections';
import { useGoals } from 'features/goals';
import { useLocalizedLifeAreas } from 'features/life-areas';
import React, { useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Plus } from 'shared/assets/icons';
import { Sizes, Variants } from 'shared/components/ui/button';
import { Chip, Colors, Sizes as ChipSizes } from 'shared/components/ui/chip';
import { Divider } from 'shared/components/ui/divider';
import { useOpenPremiumDialog } from 'shared/contexts/premium-dialog';
import { useExternalConnectionsCalendarsQueries } from 'shared/hooks/queries/use-external-connections-calendars-queries';

import { CalendarReconnectNotice } from './calendar-reconnect-notice';
import {
  CalendarSyncFormDialog,
  CalendarSyncFormValue,
} from './calendar-sync-form-dialog';
import { CalendarSyncOptionRow } from './calendar-sync-option-row';
import * as Styled from './calendar-sync-options.style';

export const CalendarSyncOptionsList: React.FC = () => {
  const { t } = useTranslation();
  const connections = useExternalCalendarConnections();
  const syncs = useCalendarSyncs();
  const goals = useGoals();
  const lifeAreas = useLocalizedLifeAreas();
  const setCalendarSync = useSetCalendarSync();
  const removeCalendarSync = useRemoveCalendarSync();
  const { allowed } = useTwoWaySyncAllowed();
  const openPremiumDialog = useOpenPremiumDialog();

  // Query every connection, including non-write-capable ones: getCalendars re-derives
  // capabilities from the provider's actually-granted scope and writes them back, so this call
  // is what heals a connection whose stored capabilities are missing or stale.
  const connectionIds = useMemo(
    () => connections.map(({ id }) => id),
    [connections],
  );
  const connectionsNeedingReconnect = useMemo(
    () => getConnectionsNeedingReconnect(connections),
    [connections],
  );
  const canSyncTasks = connections.some(canConnectionWriteEvents);
  const { calendars, isPending: isLoadingCalendars } =
    useExternalConnectionsCalendarsQueries(connectionIds);

  // Only a write-capable connection can host a task sync, so its calendars are the only ones
  // offered — but every calendar stays available for resolving an already-configured row.
  const selectableCalendars = useMemo(
    () => getWritableConnectionCalendars(connections, calendars),
    [calendars, connections],
  );

  const allTasksLabel = t('calendar.sync-tasks.all-tasks');
  const sortedSyncs = useMemo(
    () =>
      getSortedCalendarSyncs(syncs, {
        goals,
        lifeAreas,
        calendars,
        allTasksLabel,
      }),
    [syncs, goals, lifeAreas, calendars, allTasksLabel],
  );

  const [isFormOpen, setIsFormOpen] = useState(false);

  const onAddRow = () => setIsFormOpen(true);
  const onCancelForm = () => setIsFormOpen(false);

  const onSaveForm = (value: CalendarSyncFormValue) => {
    const { set } = getCalendarSyncWriteOps({
      current: null,
      next: {
        connectionId: value.calendar.connectionId,
        linkedItemId: value.linkedItemId,
        linkedItemType: value.linkedItemType,
        calendarId: value.calendar.calendarId,
        mode: value.mode,
        detailsVisibility: value.detailsVisibility,
      },
      change: CalendarSyncChange.Target,
      referenceDate: new Date(),
    });
    setCalendarSync(set);
    setIsFormOpen(false);
  };

  if (!connections.length) {
    return null;
  }

  return (
    <Styled.Section>
      <Divider />

      <Styled.Title>
        <Typography variant="h6" component="h3">
          {t('calendar.sync-tasks.title')}
        </Typography>
        <Chip color={Colors.Warning} size={ChipSizes.Small} label="Beta" />
      </Styled.Title>

      <CalendarReconnectNotice connections={connectionsNeedingReconnect} />

      {/* Configured rows stay visible (and removable) even when every
          connection has lost write capability; only creating new syncs needs it. */}
      {!!sortedSyncs.length && (
        <Styled.Table>
          <Styled.HeaderRow>
            <Styled.ColumnLabel>
              {t('calendar.sync-tasks.fields.tasks')}
            </Styled.ColumnLabel>
            <span />
            <Styled.ColumnLabel>
              {t('calendar.sync-tasks.fields.calendar')}
            </Styled.ColumnLabel>
            <Styled.ColumnLabel>
              {t('calendar.sync-tasks.fields.task-sync')}
            </Styled.ColumnLabel>
            <Styled.ColumnLabel>
              {t('calendar.sync-tasks.fields.task-details')}
            </Styled.ColumnLabel>
            <span />
          </Styled.HeaderRow>

          {sortedSyncs.map((sync) => {
            const applyChange = (
              next: Omit<CalendarSyncWrite, 'syncTasksFrom'>,
              change: CalendarSyncChange,
            ) => {
              const { remove, set } = getCalendarSyncWriteOps({
                current: sync,
                next,
                change,
                referenceDate: new Date(),
              });
              if (remove) {
                removeCalendarSync(remove);
              }
              setCalendarSync(set);
            };

            const onChangeLinkedItem = (
              link: { id: ID; type: LinkedItemType } | null,
            ) => {
              if (!link) {
                removeCalendarSync({
                  connectionId: sync.connectionId,
                  linkedItemId: sync.linkedItemId,
                });
                return;
              }
              applyChange(
                {
                  connectionId: sync.connectionId,
                  linkedItemId: link.id,
                  linkedItemType: link.type,
                  calendarId: sync.calendarId,
                  mode: sync.mode,
                  detailsVisibility: sync.detailsVisibility,
                },
                CalendarSyncChange.Target,
              );
            };
            const onChangeCalendar = (value: CalendarRef) =>
              applyChange(
                {
                  connectionId: value.connectionId,
                  linkedItemId: sync.linkedItemId,
                  linkedItemType: sync.linkedItemType,
                  calendarId: value.calendarId,
                  mode: sync.mode,
                  detailsVisibility: sync.detailsVisibility,
                },
                CalendarSyncChange.Calendar,
              );
            const onChangeMode = (mode: SyncMode) =>
              applyChange(
                {
                  connectionId: sync.connectionId,
                  linkedItemId: sync.linkedItemId,
                  linkedItemType: sync.linkedItemType,
                  calendarId: sync.calendarId,
                  mode,
                  detailsVisibility: sync.detailsVisibility,
                },
                CalendarSyncChange.Settings,
              );
            const onChangeVisibility = (
              detailsVisibility: CalendarDetailsVisibility,
            ) =>
              applyChange(
                {
                  connectionId: sync.connectionId,
                  linkedItemId: sync.linkedItemId,
                  linkedItemType: sync.linkedItemType,
                  calendarId: sync.calendarId,
                  mode: sync.mode,
                  detailsVisibility,
                },
                CalendarSyncChange.Settings,
              );
            const onRemove = () =>
              removeCalendarSync({
                connectionId: sync.connectionId,
                linkedItemId: sync.linkedItemId,
              });

            return (
              <CalendarSyncOptionRow
                key={`${sync.connectionId}:${sync.linkedItemId}`}
                linkedItemId={sync.linkedItemId}
                calendar={{
                  connectionId: sync.connectionId,
                  calendarId: sync.calendarId,
                }}
                mode={sync.mode}
                detailsVisibility={sync.detailsVisibility ?? null}
                goals={goals}
                lifeAreas={lifeAreas}
                calendars={calendars}
                selectableCalendars={selectableCalendars}
                isLoadingCalendars={isLoadingCalendars}
                onChangeLinkedItem={onChangeLinkedItem}
                onChangeCalendar={onChangeCalendar}
                onChangeMode={onChangeMode}
                onChangeVisibility={onChangeVisibility}
                onRemove={onRemove}
              />
            );
          })}
        </Styled.Table>
      )}

      {isFormOpen && (
        <CalendarSyncFormDialog
          goals={goals}
          lifeAreas={lifeAreas}
          calendars={calendars}
          selectableCalendars={selectableCalendars}
          isLoadingCalendars={isLoadingCalendars}
          onCancel={onCancelForm}
          onSave={onSaveForm}
        />
      )}

      {allowed ? (
        <Styled.AddButton
          type="button"
          variant={Variants.Text}
          size={Sizes.Medium}
          startIcon={Plus}
          onClick={onAddRow}
          disabled={!canSyncTasks}
        >
          {t('calendar.sync-tasks.add')}
        </Styled.AddButton>
      ) : (
        <Styled.Upsell>
          <Trans
            i18nKey="calendar.sync-tasks.upsell"
            components={{
              premium: (
                <Styled.PremiumLink
                  key="premium"
                  type="button"
                  onClick={openPremiumDialog}
                />
              ),
            }}
          />
        </Styled.Upsell>
      )}
    </Styled.Section>
  );
};
